import { BREAKPOINTS } from './breakpoints';
import type { MaterialTokens } from './presets/cardMaterial';

export type SurfaceRole = 'reading' | 'panel' | 'overlay';

export interface ThemeTokens {
  mode: 'light' | 'dark';
  color: {
    brandFrom: string;
    brandTo: string;
    accentCyan: string;
    accentCyanDim: string;
    accentMagenta: string;
    accentAmber: string;
    /**
     * The paid supporter mark's colour — the ONLY consumer is the supporter
     * badge (icons/brand/SupporterBadge). Deliberately NOT an accent token:
     * `accentAmber` is a stale name from the purple retheme and resolves to
     * the same purple as `accentCyan` (which is what the verified check
     * renders in), so a badge painted with it was colour-identical to the
     * verified check. This token is a gold that never participates in the
     * accent-preset system (applyPreferences rewrites the accent* tokens but
     * passes this one straight through), so the supporter badge and the
     * verified check can never collapse onto one colour via personalisation.
     * Colour is never the only signal — the two marks are also different
     * SHAPES (five-point star vs. scalloped badge-check).
     */
    supporter: string;
    /**
     * The Council's review state — the ONE colour that says "this is on the
     * record". Reserved exactly like `supporter`: applyPreferences passes it
     * straight through, so no accent preset and no custom hex can repaint it.
     *
     * This exists because `accentAmber` (which PostStatus asked for via
     * $tone="amber") is a stale name from the purple retheme that resolves to
     * the SAME purple as accentCyan — so the platform's whole thesis surface
     * rendered in the Like button's colour, and picking a custom accent moved
     * it. Moderation state is not decoration and must not follow personalisation.
     */
    review: string;
    /**
     * 1.27.70: a reminder / caution yellow (the Videos pages' two-factor flyout). Reserved like
     * `review`: applyPreferences passes it straight through, so no accent preset repaints it.
     * `accentAmber` is NOT yellow (a stale name, it resolves to the accent purple), which is why
     * this exists. `caution` is readable as text and icons on `surface`; `cautionSoft` is an
     * OPAQUE banner fill (glass-off rule); `onCaution` is text on a `caution` fill.
     */
    caution: string;
    cautionSoft: string;
    onCaution: string;
    /**
     * Cosmetic profile-flair ring colours (1.23.00 Phase 6). Tokenised so the
     * rings are tuned per theme instead of using one dark-theme-only hex in
     * both (they washed out on the light surface). Keyed by the server's
     * UserPreferences.FLAIR values — see features/profile/ProfileIdentity.
     */
    flairAurora: string;
    flairEmber: string;
    flairCyan: string;
    /**
     * The shell chrome plane (rail, topbar, mobile top bar, bottom tab bar).
     * Sits one step BELOW `surface` in both modes so the reading column is the
     * brightest region on screen without any painted scrim — which is what let
     * the hand-authored ±400px backdrop blackout strip be deleted.
     */
    bgRoot: string;
    surfaceChrome: string;
    surface: string;
    surfaceRaised: string;
    glassBg: string;
    glassBorder: string;
    hover: string;
    hairSoft: string;
    textPrimary: string;
    textMuted: string;
    textFaint: string;
    glow: string;
    danger: string;
    success: string;
    focusRing: string;
    safety: string;
    privacy: string;
    freedom: string;
    onAccent: string;
  };
  radius: { sm: string; md: string; lg: string; pill: string };
  /**
   * `bodyWeight` is a per-MODE variable-font weight, not a size: light text on a
   * dark ground optically blooms and reads thinner than the same weight on
   * paper, so dark mode sets 430 against light mode's 400. Consumed by
   * GlobalStyle on `body`; Schibsted Grotesk carries a continuous wght axis so
   * 430 is a real instance, not a synthesised bold.
   */
  font: { display: string; body: string; mono: string; bodyWeight: number };
  fontSize: { xs: string; sm: string; md: string; lg: string; xl: string; display: string };
  /** Line boxes paired with each font-size step; both scale with text size. */
  lineHeight: { xs: string; sm: string; md: string; lg: string; xl: string; display: string };
  elevation: { sm: string; md: string; glow: string };
  breakpoint: { mobile: number; desktop: number };
  /**
   * Accent-FILL finish (Button primary + elevation.glow only). NOT a surface treatment —
   * cards use `material` below.
   */
  finish: 'flat' | 'metallic' | 'glow';
  /** Physical card-material tokens. ONLY the three card primitives read these. */
  material: MaterialTokens;
  /** Glass cards mode — true when the cards-overlay pref is OFF (bg shows through). */
  cardsGlass: boolean;
  /** Contrast-resolved card gradient. Transparent stops preserve glass mode. */
  surfaceGradient: string;
  surfaceRole: SurfaceRole;
  surfaceGradients: Record<SurfaceRole, string>;
  /** Glass blur radius in px for the card `backdrop-filter` (personalisation). */
  glassBlur: number;
  /** Glass backdrop saturation multiplier for the card `backdrop-filter` (personalisation). */
  glassSaturation: number;
  /** Accessibility: underline anchor links app-wide (GlobalStyle reads this). */
  underlineLinks: boolean;
}

const shared = {
  // Base radius steps are UNCHANGED (8/12/16) on purpose: applyPreferences
  // computes radiusMult = radiusPx / parseFloat(radius.lg), so moving `lg` would
  // silently rescale every account that has already touched the radius slider.
  radius: { sm: '8px', md: '12px', lg: '16px', pill: '999px' },
  font: {
    // Schibsted Grotesk for display AND body (was Space Grotesk for both).
    // One family, two jobs: a second display face is the "font pairing
    // showcase" tell, and Space Grotesk is named on every 2026 generated-UI
    // font list. Schibsted was drawn for a news publisher's reading
    // interfaces and ships tnum + a slashed zero.
    // Mono stays JetBrains Mono, now restricted to DATA (handles, counts,
    // timestamps, ids) — never prose, never uppercase tracked labels.
    display: "'Schibsted Grotesk', system-ui, sans-serif",
    body: "'Schibsted Grotesk', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
    bodyWeight: 400,
  },
  fontSize: { xs: '12px', sm: '14px', md: '16px', lg: '20px', xl: '24px', display: '32px' },
  lineHeight: { xs: '16px', sm: '20px', md: '24px', lg: '28px', xl: '32px', display: '40px' },
  breakpoint: BREAKPOINTS,
  finish: 'flat',
  cardsGlass: false,
  surfaceGradient: 'none',
  surfaceRole: 'reading',
  surfaceGradients: { reading: 'none', panel: 'none', overlay: 'none' },
  glassBlur: 12,
  glassSaturation: 1,
  underlineLinks: false,
} as const;

export const darkTheme: ThemeTokens = {
  mode: 'dark',
  color: {
    // "Indelible" damson — the copying-pencil / stamp-pad violet that registries
    // used because the mark could not be erased. It is a MARK, not a wash: the
    // only sanctioned uses are the primary fill, the active nav rule, links and
    // the focus ring. oklch(0.70 0.11 318) — half the chroma of the old #B57BFF
    // and 18° toward plum, which puts it outside the indigo/violet band
    // (H 270–310 at C > 0.12) that every 2026 source names as a generated-UI tell.
    // 6.6:1 on `surface`, 6.7:1 against `onAccent`.
    brandFrom: '#BB88CB',
    brandTo: '#9A66AC',
    accentCyan: '#BB88CB',
    accentCyanDim: '#9A66AC',
    accentMagenta: '#C79BD4',
    accentAmber: '#9A66AC',
    // Supporter gold — a different hue family from the accent, so the supporter
    // star and the verified check can never collapse onto one colour.
    supporter: '#FFB43C',
    // Council review ochre — reserved, never rewritten by a preset. Distinct
    // from `supporter` gold (deeper, browner) and from `danger`. 7.4:1 on `surface`.
    review: '#D9A441',
    // Caution yellow (1.27.70) — brighter and yellower than the review ochre.
    caution: '#EBCB52',
    cautionSoft: '#2C291B',
    onCaution: '#1A1606',
    // Flair rings desaturated so they sit on the graphite without vibrating —
    // still three distinct hue families, still 2px rings.
    flairAurora: '#9A8AE6',
    flairEmber: '#E07A5F',
    flairCyan: '#4FB8C6',
    // Near-neutral graphite carrying the ink's hue at chroma ≈0.003: below the
    // threshold where anyone reads it as a colour, above the point where greys
    // look inherited. Deliberately off hue 265 (a popular framework's default
    // slate) and off the warm-carbon hue of the 2026 "tasteful" default.
    bgRoot: '#0C0C0E',
    surfaceChrome: '#100F12',
    surface: '#151517',
    surfaceRaised: '#1C1C1F',
    // Hairlines are NEUTRAL now, not accent-tinted. The purple outline on every
    // card edge was the single biggest reason the screen read as one purple wash.
    glassBg: 'rgba(18, 18, 21, 0.72)',
    glassBorder: 'rgba(255, 255, 255, 0.085)',
    hover: 'rgba(255, 255, 255, 0.055)',
    hairSoft: 'rgba(255, 255, 255, 0.16)',
    textPrimary: '#E9E7EA',
    textMuted: '#97949B',
    textFaint: '#67646B',
    // Semantic colours off neon: #ff5d6c / #2fe3a6 were maximum-chroma display
    // reds and greens. These clear 5.6:1 and 9.2:1 on the card as text.
    glow: 'rgba(187, 136, 203, 0.30)',
    danger: '#E5626F',
    success: '#4FCB9A',
    focusRing: 'rgba(187, 136, 203, 1)',
    // The triad is the logo's own three hexes (LogoTriad.tsx), not a second
    // set that drifted from them. The mark is the single source of truth.
    safety: '#FF3D52',
    privacy: '#8A4FE0',
    freedom: '#FFA52E',
    onAccent: '#14121A',
  },
  ...shared,
  font: { ...shared.font, bodyWeight: 430 },
  elevation: {
    // No coloured halo. `glow` is now the FOCUS ring only — a neutral gap ring
    // that survives any user card colour — not a decorative bloom on every
    // button, avatar, toggle and tooltip.
    sm: '0 1px 0 rgba(0, 0, 0, 0.25)',
    md: '0 8px 24px rgba(0, 0, 0, 0.35)',
    glow: '0 0 0 2px #151517, 0 0 0 4px rgba(187, 136, 203, 1)',
  },
  material: {
    sheen: 0,
    grain: 0,
    edge: 0,
    elevation: 0.5,
    backgroundImage: 'none',
    backgroundSize: 'auto',
    backgroundRepeat: 'repeat',
    shadow: '0 1px 0 rgba(0, 0, 0, 0.25)',
    shadowElevated: '0 8px 24px rgba(0, 0, 0, 0.35)',
  },
};

export const lightTheme: ThemeTokens = {
  mode: 'light',
  color: {
    // Damson ink re-solved for paper: oklch(0.45 0.13 318). 8.5:1 on the white
    // card and 7.3:1 on the paper ground as TEXT, and white-on-damson FILLS read
    // at the same 8.5:1. Same mark-not-wash rule as dark.
    brandFrom: '#6E3480',
    brandTo: '#5A2A6A',
    accentCyan: '#6E3480',
    accentCyanDim: '#5A2A6A',
    accentMagenta: '#5A2A6A',
    accentAmber: '#6E3480',
    // Supporter bronze — 5.9:1 on white, and a different hue family from damson.
    supporter: '#8A5A00',
    // Council review, re-solved for paper: 5.1:1 on white. Deeper than
    // `supporter` bronze so the two marks stay tellable apart on the light theme.
    review: '#8A5A00',
    // Caution, re-solved for paper: an olive yellow that clears 4.5:1 on white, on a pale fill.
    caution: '#716100',
    cautionSoft: '#FFF1BF',
    onCaution: '#FFFFFF',
    flairAurora: '#5B3FD6',
    flairEmber: '#C2410C',
    flairCyan: '#0E7490',
    // Register paper, not cream: the warm cream/beige ground is the 2026
    // "tasteful AI" tell. Near-neutral with the same ink hue at chroma ≈0.003.
    bgRoot: '#EFEDEE',
    surfaceChrome: '#E6E4E6',
    surface: '#FFFFFF',
    surfaceRaised: '#FFFFFF',
    glassBg: 'rgba(255, 255, 255, 0.74)',
    glassBorder: 'rgba(20, 18, 22, 0.13)',
    hover: 'rgba(20, 18, 22, 0.05)',
    hairSoft: 'rgba(20, 18, 22, 0.24)',
    textPrimary: '#17151A',
    textMuted: '#5A565F',
    textFaint: '#8A868F',
    // success deepened from #0f9d6c: that sat at 3.5:1 on the white card and is
    // used as text ("Kept visible"), a pre-existing AA gap the token test now
    // catches. #0B7A55 clears 5.3:1.
    glow: 'rgba(110, 52, 128, 0.16)',
    danger: '#d11f3f',
    success: '#0B7A55',
    focusRing: 'rgba(110, 52, 128, 1)',
    safety: '#C32945',
    privacy: '#7A45C8',
    freedom: '#9A6600',
    onAccent: '#FFFFFF',
  },
  ...shared,
  font: { ...shared.font, bodyWeight: 400 },
  elevation: {
    sm: '0 1px 0 rgba(20, 18, 22, 0.08)',
    md: '0 8px 24px rgba(20, 18, 22, 0.14)',
    glow: '0 0 0 2px #FFFFFF, 0 0 0 4px rgba(110, 52, 128, 1)',
  },
  material: {
    sheen: 0,
    grain: 0,
    edge: 0,
    elevation: 0.5,
    backgroundImage: 'none',
    backgroundSize: 'auto',
    backgroundRepeat: 'repeat',
    shadow: '0 1px 0 rgba(20, 18, 22, 0.08)',
    shadowElevated: '0 8px 24px rgba(20, 18, 22, 0.14)',
  },
};
